import type { Resident } from '@lmc/ts-common'
import type React from 'react'
import { PureComponent } from 'react'
import styled from 'styled-components'
import { getNominative } from '../../../common/utils'
import {
  ProfilePicture,
  ResidentSelectOption,
  SearchSelect
} from '../../generic'

interface ModalResidentSelectorProps {
  goToNextStep?: () => void
  data: {
    residents: Resident[]
  }
  setFormField: (data: { key: string; value: string }) => void
  style?: React.CSSProperties
  features: {
    preferredPeopleNominative: string
  }
  testId?: string
}

export default class ModalResidentSelector extends PureComponent<ModalResidentSelectorProps> {
  handleSelect = (resident: Resident | null) => {
    const { goToNextStep, setFormField } = this.props
    if (!resident) return
    setFormField({ key: 'resident', value: resident.id })
    if (goToNextStep) goToNextStep()
  }

  renderResident = (resident: Resident, index: number) => (
    <ResidentItem
      key={resident.id}
      onClick={() => this.handleSelect(resident)}
      data-testid={
        this.props.testId
          ? `${this.props.testId}-resident-${index}`
          : `modal-resident-selector-resident-${index}`
      }
    >
      <SelectableContainer>
        <SelectableIcon
          className="lmc-selectable-icon"
          url={resident.picture}
          size={100}
        />
      </SelectableContainer>
      <ResidentName>
        {resident.name.first} {resident.name.last}
      </ResidentName>
    </ResidentItem>
  )

  render(): JSX.Element {
    const {
      data: { residents },
      style,
      testId
    } = this.props
    const preferredResidentTerm = getNominative(
      this.props.features.preferredPeopleNominative,
      'Resident'
    )
    return (
      <div
        style={{ ...style, margin: '0 auto', maxWidth: 720 }}
        data-testid={testId || 'modal-resident-selector'}
      >
        <TitleContainer
          data-testid={
            testId ? `${testId}-title` : 'modal-resident-selector-title'
          }
        >
          {`Choose a ${preferredResidentTerm}`}
        </TitleContainer>
        <SearchContainer
          data-testid={
            testId
              ? `${testId}-search-container`
              : 'modal-resident-selector-search-container'
          }
        >
          <SearchSelect
            formatOptionLabel={ResidentSelectOption}
            getOptionLabel={(resident: Resident) =>
              `${resident.name.first} ${resident.name.last}`
            }
            getOptionValue={(resident: Resident) => resident.id}
            options={residents}
            placeholder="Search by name"
            onChange={this.handleSelect}
            data-testid={
              testId ? `${testId}-search` : 'modal-resident-selector-search'
            }
          />
        </SearchContainer>
        <ResidentsContainer
          data-cy="residents-list"
          data-testid={
            testId
              ? `${testId}-residents-list`
              : 'modal-resident-selector-residents-list'
          }
        >
          {residents.map((resident, index) =>
            this.renderResident(resident, index)
          )}
        </ResidentsContainer>
      </div>
    )
  }
}

const ResidentItem = styled.div`
  cursor: pointer;
  margin: 0px 24px 24px;

  &:hover .lmc-selectable-icon {
    border-color: rgba(255, 255, 255, 0.5);
  }
`

const ResidentName = styled.span`
  display: block;
  hyphens: auto;
  margin-top: 10px;
  max-width: 100px;
  text-align: center;
  word-break: break-word;
`

const ResidentsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 50px 0px 0px auto;
`

const SearchContainer = styled.div`
  padding-top: 24px;
`

const SelectableContainer = styled.div`
  cursor: pointer;
`

const SelectableIcon = styled(ProfilePicture)`
  border: 3px solid rgba(255, 255, 255, 0);
  transition: border-color 0.2s ease-in-out;
`

const TitleContainer = styled.div`
  font-size: 28px;
  font-weight: 300;
  text-align: center;
`
